import {replaceCaptionWordBody} from '@waka/compat/caption-word-text';
import {planCaptionCaretSplit,readCaptionCaret} from '@waka/compat/caption-caret-split';
import {
  findCaptionPanelTrackOwner,
  getCaptionPanelSceneGroups,
  getCaptionPanelNextItemIds,
  getVisibleCaptionPanelItemIds,
} from '@waka/compat/caption-panel-tracks';
import {projectCaptionViewWindow} from '@waka/compat/caption-view-window';
import {projectCaptionPanelPlayback} from '@waka/compat/caption-panel-playback';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useAllItems,
  useAssets,
  useCurrentStateAsRef,
  useFps,
  useFullState,
  useLinkingEnabled,
  useTracks,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';
import { PlayerRef } from '@remotion/player';
import { useTimelinePosition } from '@/features/remotion-editor/editor/utils/use-timeline-position';
import { useTranslations } from 'next-intl';
import {
  Checkbox,
  Chip,
  GhostButton,
  Label,
  toast,
  ToolButton,
  Tooltip,
} from '@crabs-ai/ds-react';
import { Delete, HelpFill } from '@crabs-ai/web-icons';

import { deleteItems } from '@/features/remotion-editor/editor/state/actions/delete-items';
import { splitItem } from '@/features/remotion-editor/editor/state/actions/split-item';
import { toggleLinking } from '@/features/remotion-editor/editor/state/actions/toggle-linking';
import { syncEditedCaptionsToStore } from '@/features/remotion-editor/editor/state/actions/caption-translation-actions';
import { saveLinkingEnabled } from '@/features/remotion-editor/editor/state/linking-persistance';
import { convertMsToFrame } from '@/features/remotion-editor/editor/utils/convert-ms-to-frame';
import { renderTimesMs } from '@/features/remotion-editor/editor/utils/render-times-ms';
import ScriptPropertyPanelContentDivider from '@/features/remotion-editor/editor/editor-property-panel/ui/common/ScriptPropertyPanelContentDivider';
import CaptionWord from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/captionns/CaptionWord';
import {
  CaptionAsset,
  isCaptionAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import {
  CaptionsItem,
  isCaptionsItem,
} from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import {
  getWordBeforeCaret,
  getCaretWordBoundary,
} from '@/features/remotion-editor/editor/editor-property-panel/utils/word';
import {
  addCaptionWordLineBreakWithinAsset,
  calculateCaptionAssetSize,
  canMergeCaptionItems,
  isCaptionPresentationMergeBoundary,
  isolateWordForDeletion,
  mergeCaptionItems,
  mergeCaptionWordWithinAsset,
  removeCaptionWordLineBreakWithinAsset,
  splitCaptionItem,
  splitCaptionWordWithinAsset,
  unmergeCaptionPresentationBoundary,
} from '@/features/remotion-editor/editor/editor-property-panel/utils/caption';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';
import { DtoProjectType } from '@/shared/constants';

interface CaptionsContentProps {
  playerRef: React.RefObject<PlayerRef | null>;
  isTranscriptOnly?: boolean;
  isShowTranscriptCheckbox?: boolean;
  isEditable?: boolean;
}

type CaptionWordEntry = {
  index: number;
  word: string;
  rawText: string;
  displayWord: string;
  absStart: number;
  absEnd: number;
  hasLineBreakAfter: boolean;
};

type CaptionContext = {
  item: CaptionsItem;
  asset: CaptionAsset;
  sceneId: number | null;
  videoStart: number;
  videoEnd: number;
  words: CaptionWordEntry[];
  displayWords: CaptionWordEntry[];
};

/** 잘못된 ms 값을 0으로 보정한다. */
const safeMs = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** 끝 시점이 시작 시점 이하인 경우 최소 1ms 보정한다. */
const ensureEnd = (start: number, end: number) =>
  end > start ? end : start + 1;

/** leading space는 한 칸으로 보수적으로 정규화한다. */
const trimLeadingSpace = (value: string) => value.replace(/^\s+/, '');

/** Remotion timeline 프레임 기준을 절대 ms로 맞추기 위한 보조 함수. */
const convertFrameToMs = (frame: number, fps: number) => {
  if (!Number.isFinite(frame) || fps <= 0) {
    return 0;
  }

  return Math.round((frame / fps) * 1000);
};

/**
 * 캡션 asset을 화면에 노출할 단어 엔트리로 변환한다.
 * - leading space는 그대로 유지하되, trailing newline은 표시용 플래그로 분리한다.
 * - remotion에서 제공하는 timing을 기반으로 절대 ms 범위를 산출한다.
 */
const buildWordEntries = (
  asset: CaptionAsset,
  targetVideoStartMs: number,
  originVideoStartMs: number,
  projected = asset.captions.map((caption, index) => ({caption, index})),
): CaptionWordEntry[] => {
  const originStart = safeMs(originVideoStartMs);
  const baseStart = Math.max(0, safeMs(targetVideoStartMs));
  // 사용자가 타임라인에서 from을 이동하면 단어 타이밍도 동일 오프셋만큼 이동해야 한다.
  const offset = baseStart - originStart;

  return projected.map(({caption, index}) => {
    const rawText = caption.text ?? '';
    const absStartRaw = safeMs(caption.startMs) + offset;
    const absEndRaw = safeMs(caption.endMs) + offset;
    const absStart = absStartRaw >= baseStart ? absStartRaw : baseStart;
    const absEnd = ensureEnd(absStart, absEndRaw);

    const trimmedLeading = trimLeadingSpace(rawText);
    // rawText 끝에 newline이 포함되어 있으면 UI에서 줄바꿈으로 표현한다.
    const hasLineBreakAfter = /(?:\r?\n)\s*$/.test(trimmedLeading);
    const displayWord = hasLineBreakAfter
      ? trimmedLeading.replace(/(?:\r?\n)\s*$/, '')
      : trimmedLeading;

    return {
      index,
      rawText,
      displayWord,
      word: trimmedLeading.trim(),
      absStart,
      absEnd,
      hasLineBreakAfter,
    };
  });
};

type CaptionItemRowProps = {
  id: string;
  context: CaptionContext;
  nextItemId: string | undefined;
  activeWordIndexes: string;
  trackLocked: boolean;
  trackHidden: boolean;
  isShowTranscriptOnly: boolean;
  isEditable: boolean;
  isLinkingEnabled: boolean;
  fullState: ReturnType<typeof useFullState>;
  projectType: ReturnType<typeof useProjectTypeContext>['projectType'];
  deleteLabel: string;
  mergeLabel: string;
  unmergeLabel: string;
  onDeleteCaptionItem: (id: string) => void;
  onMergeCaptionItems: (previousId: string, currentId: string) => boolean;
  onUnmergeCaptionItems: (previousId: string, currentId: string) => void;
  onWordClick: React.ComponentProps<typeof CaptionWord>['onWordClick'];
  onWordKeyDown: React.ComponentProps<typeof CaptionWord>['onWordKeyDown'];
  onWordInput: React.ComponentProps<typeof CaptionWord>['onInput'];
};

/** Playback changes only the active indexes, so inactive rows keep their memo inputs. */
const CaptionItemRow = React.memo(function CaptionItemRow({
  id, context, nextItemId, activeWordIndexes, trackLocked, trackHidden,
  isShowTranscriptOnly, isEditable, isLinkingEnabled, fullState, projectType,
  deleteLabel, mergeLabel, unmergeLabel, onDeleteCaptionItem,
  onMergeCaptionItems, onUnmergeCaptionItems, onWordClick, onWordKeyDown, onWordInput,
}: CaptionItemRowProps) {
  const {videoStart, videoEnd, displayWords} = context;
  const isMergedWithNextItem = nextItemId != null && isCaptionPresentationMergeBoundary({
    state: fullState, previousItemId: id, currentItemId: nextItemId, projectType,
  });
  const canMergeWithNextItem = isEditable &&
    projectType === DtoProjectType.agentVideoCaption &&
    nextItemId != null && !isMergedWithNextItem &&
    canMergeCaptionItems({state: fullState, previousItemId: id, currentItemId: nextItemId, projectType});

  return (
    <>
      <div className="flex flex-col gap-5xs px-2xs">
        {!isShowTranscriptOnly && (
          <div className="flex items-center justify-between gap-4xs">
            <span className="text-title-small text-sys-cont-neutral-default">
              {`[${renderTimesMs(videoStart)}-${renderTimesMs(videoEnd)}]`}
            </span>
            {isEditable && isLinkingEnabled && !trackLocked && (
              <ToolButton size="sm" onClick={() => onDeleteCaptionItem(id)}
                title={deleteLabel} aria-label={deleteLabel}>
                <Delete className="text-sys-cont-neutral-tertiary hover:text-sys-cont-error-default size-4" />
              </ToolButton>
            )}
          </div>
        )}
        <div className="flex flex-wrap">
          {displayWords.map((wordEntry) => {
            const isActive = !trackHidden && activeWordIndexes.includes(`,${wordEntry.index},`);
            const wordKey = `${id}-${wordEntry.index}`;
            return (
              <React.Fragment key={wordKey}>
                <CaptionWord
                  word={wordEntry.word}
                  displayWord={wordEntry.displayWord}
                  isActive={isActive}
                  absStart={wordEntry.absStart}
                  itemId={id}
                  wordId={wordKey}
                  wordIndex={wordEntry.index}
                  onWordClick={onWordClick}
                  onWordKeyDown={onWordKeyDown}
                  onInput={onWordInput}
                  isEditable={isEditable && !trackLocked}
                  isLinkingEnabled={isLinkingEnabled}
                />
                {wordEntry.hasLineBreakAfter ? (
                  <span aria-hidden="true" className="h-0 basis-full leading-none" />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <ScriptPropertyPanelContentDivider>
        {isEditable && isMergedWithNextItem && nextItemId ? (
          <GhostButton type="button" size="small" hierarchy="tertiary"
            onClick={() => onUnmergeCaptionItems(id, nextItemId)}>
            {unmergeLabel}
          </GhostButton>
        ) : canMergeWithNextItem && nextItemId ? (
          <GhostButton type="button" size="small" hierarchy="tertiary"
            onClick={() => onMergeCaptionItems(id, nextItemId)}>
            {mergeLabel}
          </GhostButton>
        ) : null}
      </ScriptPropertyPanelContentDivider>
    </>
  );
});

/**
 * 캡션 편집 패널 UI를 렌더링한다.
 * - Remotion timeline과 동기화된 단어 목록을 표시하고, 각종 키 입력으로 단어 분할/병합/개행을 제어한다.
 * - Shift+Enter 단어 개행, Backspace 개행 취소 등 사용자 편집 경험을 한 곳에서 관리한다.
 */
function CaptionsContent({
  playerRef,
  isTranscriptOnly: isTranscriptOnlyProps = false,
  isShowTranscriptCheckbox = true,
  isEditable = true,
}: CaptionsContentProps) {
  const t = useTranslations();

  const { tracks } = useTracks();
  const { items } = useAllItems();
  const { assets } = useAssets();
  const { fps } = useFps();
  const fullState = useFullState();
  const currentStateRef = useCurrentStateAsRef();
  const { setState } = useWriteContext();
  const { projectType } = useProjectTypeContext();
  const currentFrame = useTimelinePosition({ playerRef });
  const isLinkingEnabled = useLinkingEnabled();
  // Track ownership is stable across playback frames; avoid a lane scan for
  // every rendered cue on each frame of a long imported timeline.
  const captionNextItemIds = useMemo(() => getCaptionPanelNextItemIds(tracks, items), [tracks, items]);

  const sectionRef = useRef<HTMLElement | null>(null);
  const lastScrolledWordIdRef = useRef<string | null>(null);

  const [isShowTranscriptOnly, setIsShowTranscriptOnly] = useState(
    isTranscriptOnlyProps,
  );

  const handleMergeCaptionItems = useCallback(
    (previousItemId: string, currentItemId: string) => {
      const currentState = currentStateRef.current;
      if (
        !canMergeCaptionItems({
          state: currentState,
          previousItemId,
          currentItemId,
          projectType,
        })
      ) {
        return false;
      }

      setState({
        update: (state) =>
          mergeCaptionItems({
            state,
            previousItemId,
            currentItemId,
            projectType,
          }),
        commitToUndoStack: true,
      });

      return true;
    },
    [currentStateRef, projectType, setState],
  );

  const handleUnmergeCaptionItems = useCallback(
    (previousItemId: string, currentItemId: string) => {
      setState({
        update: (state) =>
          unmergeCaptionPresentationBoundary({
            state,
            previousItemId,
            currentItemId,
            projectType,
          }),
        commitToUndoStack: true,
      });
    },
    [projectType, setState],
  );

  // 아이템과 에셋 정보를 합쳐 단어/타이밍 정보를 미리 계산한다.
  const captionContexts = useMemo(() => {
    const map = new Map<string, CaptionContext>();

    Object.values(items).forEach((candidate) => {
      if (!isCaptionsItem(candidate)) {
        return;
      }

      const asset = assets[candidate.assetId];
      if (!isCaptionAsset(asset)) {
        return;
      }

      // 에셋이 처음 로드됐을 때 기준이 되는 절대 시작 ms.
      const originVideoStart = safeMs(
        asset.timelineSourceAssetId
          ? candidate.captionStartInSeconds * 1000
          : asset.videoStart ?? candidate.captionStartInSeconds * 1000,
      );
      const hasValidFps = fps > 0;
      // timeline 상에서 사용자가 이동시킨 from 값을 기반으로 현재 videoStart를 계산한다.
      const timelineVideoStart = hasValidFps
        ? convertFrameToMs(candidate.from, fps)
        : originVideoStart;
      const videoStart = Math.max(0, timelineVideoStart);
      // 원본 시작 시점과의 차이를 계산해 단어 타이밍에 동일하게 적용한다.
      const offsetFromOrigin = videoStart - originVideoStart;
      const wordEntries = buildWordEntries(asset, videoStart, originVideoStart);
      const displayWords = asset.timelineSourceAssetId
        ? buildWordEntries(asset, videoStart, originVideoStart,
            projectCaptionViewWindow(asset.captions, {
              timelineSourceAssetId: asset.timelineSourceAssetId,
              captionStartInSeconds: candidate.captionStartInSeconds,
              durationInFrames: candidate.durationInFrames,
              fps,
              captionEdgeHold: candidate.captionEdgeHold,
            }))
        : wordEntries;
      const lastWordEnd =
        wordEntries[wordEntries.length - 1]?.absEnd ?? videoStart;
      const fallbackDurationMs = hasValidFps
        ? Math.max(0, convertFrameToMs(candidate.durationInFrames, fps))
        : 0;
      const fallbackVideoEnd = videoStart + fallbackDurationMs;
      // asset.videoEnd 도 동일하게 이동해야 하므로 offset을 더해 보정한다.
      const normalizedAssetVideoEnd = safeMs(asset.videoEnd) + offsetFromOrigin;
      const videoEnd = asset.timelineSourceAssetId && hasValidFps
        ? fallbackVideoEnd
        : Math.max(
            ensureEnd(videoStart, normalizedAssetVideoEnd),
            lastWordEnd,
            fallbackVideoEnd,
          );

      const rawSceneId = asset.sceneId;
      // 음수 sceneId 는 의미가 없으므로 null 로 정규화한다.
      const normalizedSceneId =
        rawSceneId != null && rawSceneId >= 0 ? rawSceneId : null;

      map.set(candidate.id, {
        item: candidate,
        asset,
        sceneId: normalizedSceneId,
        videoStart,
        videoEnd,
        words: wordEntries,
        displayWords,
      });
    });

    return map;
  }, [assets, fps, items]);

  /**
   * 단어 편집 결과를 asset에 반영한다.
   * @param targetItemId 수정 대상 captions item id
   * @param wordIndex 편집 중인 단어 index
   * @param originWord 편집 전 단어 문자열
   * @param nextValue contentEditable에서 읽은 신규 문자열
   * - leading/trailing space를 보존하면서 본문만 교체한다.
   * - 실제 변경이 없으면 상태를 건드리지 않는다.
   */
  const handleWordSubmit = useCallback(
    (
      targetItemId: string,
      wordIndex: number,
      originWord: string,
      nextValue: string,
    ) => {
      const trimmedValue = nextValue.trim();

      setState({
        update: (prev) => {
          const prevItem = prev.undoableState.items[targetItemId];
          if (!isCaptionsItem(prevItem)) {
            return prev;
          }

          const prevAssets = prev.undoableState.assets;
          const currentAsset = prevAssets[prevItem.assetId];
          if (!isCaptionAsset(currentAsset)) {
            return prev;
          }

          const targetCaption = currentAsset.captions[wordIndex];
          if (!targetCaption) {
            return prev;
          }

          const baseCaptionText = targetCaption.text ?? '';
          const nextCaptionText = replaceCaptionWordBody(baseCaptionText,trimmedValue);
          const prevWordValue = (originWord ?? '').trim();

          if (
            prevWordValue === trimmedValue &&
            baseCaptionText === nextCaptionText
          ) {
            return prev;
          }

          const updatedCaptions = currentAsset.captions.map((caption, idx) =>
            idx === wordIndex ? { ...caption, text: nextCaptionText } : caption,
          );

          return {
            ...prev,
            undoableState: {
              ...prev.undoableState,
              assets: {
                ...prev.undoableState.assets,
                [currentAsset.id]: {
                  ...currentAsset,
                  captions: updatedCaptions,
                  size: calculateCaptionAssetSize(updatedCaptions),
                  // 번역 asset 이면 표시 중인 쪽 보관본도 같이 갱신 (design.md §7-4)
                  ...syncEditedCaptionsToStore(currentAsset, updatedCaptions),
                } as CaptionAsset,
              },
            },
          };
        },
        commitToUndoStack: true,
      });
    },
    [setState],
  );

  /**
   * 단어 클릭 시 Remotion 플레이어를 해당 단어 시작 지점으로 이동시킨다.
   * @param startInMs 단어 절대 시작 시점(ms)
   */
  const handleWordClick = useCallback(
    (startInMs?: number) => {
      if (!playerRef.current || fps <= 0 || typeof startInMs !== 'number') {
        return;
      }

      const targetFrame = convertMsToFrame(startInMs, 'start', fps);
      // +1 한 이유 ms, frame등 변환할 때 작은 오차범위로도 가라오케 표시가 안되는 경우가 있어서 임시 방편으로 넣음
      playerRef.current.seekTo(targetFrame + 1);
    },
    [fps, playerRef],
  );

  /**
   * 특정 단어 요소로 포커스를 이동하고 커서를 설정한다.
   */
  const focusWordElement = useCallback(
    (targetWordId: string, position: 'start' | 'end') => {
      // 다음 렌더 사이클에서 포커스 이동
      requestAnimationFrame(() => {
        const targetElement = document.querySelector(
          `[data-word-id="${targetWordId}"]`,
        ) as HTMLElement | null;

        if (targetElement) {
          targetElement.focus();

          // 커서 위치 설정
          const range = document.createRange();
          const selection = window.getSelection();

          if (targetElement.firstChild) {
            if (position === 'start') {
              range.setStart(targetElement.firstChild, 0);
              range.setEnd(targetElement.firstChild, 0);
            } else {
              const textLength = targetElement.textContent?.length ?? 0;
              range.setStart(targetElement.firstChild, textLength);
              range.setEnd(targetElement.firstChild, textLength);
            }
          } else {
            range.selectNodeContents(targetElement);
            range.collapse(position === 'start');
          }

          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      });
    },
    [],
  );

  /**
   * 단어 요소에서 발생하는 키 입력을 처리한다.
   * @param event contentEditable keydown 이벤트 객체
   * @param originWord 렌더 시점 단어 문자열
   * @param itemId 대상 captions item id
   * @param wordIndex 렌더 시점 단어 index
   * - Space: 단어 분할
   * - Shift+Enter: 단어 앞/뒤 개행 삽입
   * - Backspace: 개행 제거 또는 단어 병합
   * - Enter: 캡션 아이템 분할
   * - ArrowLeft/Right (Link 활성화 시): 단어 단위 이동
   * - Backspace/Delete (Link 활성화 시): 단어 단위 삭제
   */
  const handleWordKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLParagraphElement>,
      originWord: string,
      itemId: string,
      wordIndex: number,
    ) => {
      event.stopPropagation();

      if (event.nativeEvent.isComposing || event.keyCode === 229) {
        return;
      }

      const context = captionContexts.get(itemId);
      if (!context) {
        return;
      }

      // Imported overlaps are partitioned into caption lanes. Keep keyboard
      // navigation and mutations in this cue's lane instead of the first lane.
      const captionTrackOwner = findCaptionPanelTrackOwner(tracks, itemId, items);
      if (!captionTrackOwner || captionTrackOwner.track.locked) {
        return;
      }
      const { track: captionTrack, trackIndex: captionTrackIndex, itemIndex: currentIndex } =
        captionTrackOwner;
      const trackItems = captionTrack.items ?? [];

      // Plain Enter cuts the caption at the text caret, including a single
      // imported sentence token. Shift+Enter retains the existing line break.
      if (!isLinkingEnabled && isEditable && event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const caret=readCaptionCaret(event.currentTarget);
        if(!caret)return;
        const input={itemId,wordIndex,...caret};
        const checked=planCaptionCaretSplit(currentStateRef.current,input);
        if(!checked.ok){toast({type:'error',description:checked.reason});return;}
        setState({commitToUndoStack:true,update:prev=>{
          const result=planCaptionCaretSplit(prev,input);
          return result.ok?result.state:prev;
        }});
        event.currentTarget.blur();
        return;
      }

      const words = context.words;
      const displayWords = context.displayWords;
      const displayIndex = displayWords.findIndex((word) => word.index === wordIndex);

      // Link 활성화 시 단어 단위 조작
      if (isLinkingEnabled) {
        // ArrowLeft: 이전 단어로 이동
        if (event.key === 'ArrowLeft') {
          event.preventDefault();

          if (displayIndex > 0) {
            // 같은 item 내 이전 단어로 이동
            const prevWordEntry = displayWords[displayIndex - 1];
            if (prevWordEntry) {
              const prevWordKey = `${itemId}-${prevWordEntry.index}`;
              focusWordElement(prevWordKey, 'end');
            }
          } else {
            // 이전 item의 마지막 단어로 이동
            if (currentIndex > 0) {
              const prevItemId = trackItems[currentIndex - 1];
              if (prevItemId) {
                const prevContext = captionContexts.get(prevItemId);
                if (prevContext && prevContext.displayWords.length > 0) {
                  const lastWord =
                    prevContext.displayWords[prevContext.displayWords.length - 1];
                  if (lastWord) {
                    const prevWordKey = `${prevItemId}-${lastWord.index}`;
                    focusWordElement(prevWordKey, 'end');
                  }
                }
              }
            }
          }
          return;
        }

        // ArrowRight: 다음 단어로 이동
        if (event.key === 'ArrowRight') {
          event.preventDefault();

          if (displayIndex >= 0 && displayIndex < displayWords.length - 1) {
            // 같은 item 내 다음 단어로 이동
            const nextWordEntry = displayWords[displayIndex + 1];
            if (nextWordEntry) {
              const nextWordKey = `${itemId}-${nextWordEntry.index}`;
              focusWordElement(nextWordKey, 'start');
            }
          } else {
            // 다음 item의 첫 번째 단어로 이동
            if (currentIndex >= 0 && currentIndex < trackItems.length - 1) {
              const nextItemId = trackItems[currentIndex + 1];
              if (nextItemId) {
                const nextContext = captionContexts.get(nextItemId);
                if (nextContext && nextContext.displayWords.length > 0) {
                  const firstWord = nextContext.displayWords[0];
                  if (firstWord) {
                    const nextWordKey = `${nextItemId}-${firstWord.index}`;
                    focusWordElement(nextWordKey, 'start');
                  }
                }
              }
            }
          }
          return;
        }

        // Backspace/Delete: 현재 단어 삭제 (Link 활성화 시)
        // split으로 해당 단어를 분리한 후 deleteItems로 삭제
        if (event.key === 'Backspace' || event.key === 'Delete') {
          event.preventDefault();

          const currentItemIndex = currentIndex;

          // 단어가 1개뿐이면 전체 caption item 삭제
          if (words.length === 1) {
            setState({
              update: (state) =>
                deleteItems(state, [itemId], {
                  isLinkingEnabled: true,
                }),
              commitToUndoStack: true,
            });

            // 포커스 이동
            if (event.key === 'Backspace' && currentItemIndex > 0) {
              const prevItemId = trackItems[currentItemIndex - 1];
              if (prevItemId) {
                const prevContext = captionContexts.get(prevItemId);
                if (prevContext && prevContext.displayWords.length > 0) {
                  const lastWord =
                    prevContext.displayWords[prevContext.displayWords.length - 1];
                  if (lastWord) {
                    focusWordElement(`${prevItemId}-${lastWord.index}`, 'end');
                  }
                }
              }
            } else if (
              event.key === 'Delete' &&
              currentItemIndex < trackItems.length - 1
            ) {
              const nextItemId = trackItems[currentItemIndex + 1];
              if (nextItemId) {
                const nextContext = captionContexts.get(nextItemId);
                if (nextContext && nextContext.displayWords.length > 0) {
                  const firstWord = nextContext.displayWords[0];
                  if (firstWord) {
                    focusWordElement(
                      `${nextItemId}-${firstWord.index}`,
                      'start',
                    );
                  }
                }
              }
            }
            return;
          }

          // split으로 해당 단어를 분리
          const { itemIdToDelete, deleteFrameRange } = isolateWordForDeletion({
            itemId,
            wordIndex,
            trackIndex: captionTrackIndex,
            itemIndex: currentItemIndex,
            setState,
          });

          if (itemIdToDelete && deleteFrameRange) {
            // Linked main track들(video, audio)을 찾아서 같은 프레임에서 split 후 삭제
            setState({
              update: (state) => {
                let newState = state;
                const linkedItemsToDelete: string[] = [];

                // Link 대상 트랙 타입들 (video, audio)
                const linkedTrackTypes = ['video', 'audio'] as const;

                const deleteFrom = deleteFrameRange.from;
                const deleteEnd =
                  deleteFrom + deleteFrameRange.durationInFrames;

                // 각 linked track 타입에 대해 처리
                for (const trackType of linkedTrackTypes) {
                  // Main track 찾기
                  const mainTrack = newState.undoableState.tracks.find(
                    (track) =>
                      track.metadata?.isMainTrack &&
                      track.metadata?.type === trackType,
                  );

                  if (!mainTrack) continue;

                  // 해당 프레임 범위에 걸친 아이템 찾기
                  for (const itemId of mainTrack.items) {
                    const item = newState.undoableState.items[itemId];
                    if (!item) continue;

                    const itemFrom = item.from;
                    const itemEnd = itemFrom + item.durationInFrames;

                    // 삭제 범위가 아이템 범위 내에 있는지 확인
                    if (deleteFrom >= itemFrom && deleteEnd <= itemEnd) {
                      // 첫 번째 split (deleteFrom에서)
                      if (deleteFrom > itemFrom) {
                        newState = splitItem({
                          state: newState,
                          idToSplit: itemId,
                          framePosition: deleteFrom,
                        });
                      }

                      // 첫 번째 split 후 새로 생긴 아이템 찾기 (deleteFrom에서 시작하는 아이템)
                      const updatedTrack = newState.undoableState.tracks.find(
                        (track) =>
                          track.metadata?.isMainTrack &&
                          track.metadata?.type === trackType,
                      );

                      if (updatedTrack) {
                        for (const splitItemId of updatedTrack.items) {
                          const splitItem2 =
                            newState.undoableState.items[splitItemId];
                          if (!splitItem2) continue;

                          // deleteFrom에서 시작하는 아이템 찾기
                          if (splitItem2.from === deleteFrom) {
                            // 두 번째 split (deleteEnd에서)
                            if (
                              deleteEnd <
                              splitItem2.from + splitItem2.durationInFrames
                            ) {
                              newState = splitItem({
                                state: newState,
                                idToSplit: splitItemId,
                                framePosition: deleteEnd,
                              });
                            }

                            // 최종적으로 삭제할 아이템 찾기
                            const finalTrack =
                              newState.undoableState.tracks.find(
                                (track) =>
                                  track.metadata?.isMainTrack &&
                                  track.metadata?.type === trackType,
                              );

                            if (finalTrack) {
                              for (const finalItemId of finalTrack.items) {
                                const finalItem =
                                  newState.undoableState.items[finalItemId];
                                if (!finalItem) continue;

                                // deleteFrom에서 시작하고 deleteFrameRange.durationInFrames와 같은 길이의 아이템
                                if (
                                  finalItem.from === deleteFrom &&
                                  finalItem.durationInFrames ===
                                    deleteFrameRange.durationInFrames
                                ) {
                                  linkedItemsToDelete.push(finalItemId);
                                  break;
                                }
                              }
                            }
                            break;
                          }
                        }
                      }
                      break;
                    }
                  }
                }

                // Caption과 Linked items(Video, Audio) 함께 삭제
                const itemsToDelete = [itemIdToDelete, ...linkedItemsToDelete];

                return deleteItems(newState, itemsToDelete, {
                  isLinkingEnabled: false, // 이미 수동으로 연결된 아이템을 찾았으므로
                });
              },
              commitToUndoStack: true,
            });

            // 포커스 이동: 이전/다음 단어로
            if (event.key === 'Backspace') {
              if (wordIndex > 0) {
                // 이전 단어로 (원본 아이템에 남아있음)
                focusWordElement(`${itemId}-${wordIndex - 1}`, 'end');
              }
            } else {
              // Delete: 다음 단어 위치로 (인덱스가 당겨짐)
              if (wordIndex < words.length - 1) {
                // 다음 단어가 있었으면, 새로 생긴 suffix 아이템의 첫 단어
                // 하지만 삭제 후 상태가 변경되므로 정확한 위치 계산 필요
                // 일단 원본 아이템의 다음 단어 위치로
                focusWordElement(`${itemId}-${wordIndex}`, 'start');
              }
            }
          }
          return;
        }

        // Link 활성화 시 일반 텍스트 입력 차단 (단어 단위 편집만 허용)
        if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey
        ) {
          event.preventDefault();
          return;
        }
      }

      // 기존 로직 계속...
      const wordBeforeCaret = getWordBeforeCaret(event.currentTarget);
      const { start: isCaretWordStart, end: isCaretWordEnd } =
        getCaretWordBoundary(event.currentTarget);
      const sanitizedBefore = (wordBeforeCaret ?? '').trim();

      // 현재 렌더링된 words 배열은 select 시점 스냅샷이므로 index 보정이 필요하다.
      let currentWordIndex = wordIndex;
      const trimmedOrigin = originWord.trim();

      if (!words[currentWordIndex]) {
        currentWordIndex = words.findIndex(
          (word) => word.word === trimmedOrigin,
        );
      }

      const resolvedWord = words[currentWordIndex]?.word ?? '';
      if (!resolvedWord) {
        return;
      }

      const targetCaption = context.asset.captions[currentWordIndex];
      if (!targetCaption) {
        return;
      }

      const captionWord = trimLeadingSpace(targetCaption.text ?? '').trim();
      const normalizedWord = captionWord || resolvedWord;

      if (!normalizedWord) {
        return;
      }

      if (event.key === ' ' && !isCaretWordStart && !isCaretWordEnd) {
        event.preventDefault();

        // caret 기준 스페이스바는 동일 캡션 내 단어 분리 요청으로 처리.
        if (
          normalizedWord.length < 2 ||
          sanitizedBefore.length <= 0 ||
          sanitizedBefore.length >= normalizedWord.length
        ) {
          return;
        }

        const didSplit = splitCaptionWordWithinAsset({
          itemId,
          wordIndex: currentWordIndex,
          splitOffset: sanitizedBefore.length,
          setState,
        });

        if (didSplit) {
          event.currentTarget.blur();
          event.nativeEvent.stopImmediatePropagation?.();
        }

        return;
      }

      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();

        if (!isCaretWordStart && !isCaretWordEnd) {
          toast({
            type: 'error',
            description: t('Editor.toast.divide-word-error'),
            toastOptions: {
              position: 'top-center',
            },
          });
          return;
        }

        // caret 위치가 단어 앞/뒤인지에 따라 개행을 삽입할 타겟을 결정한다.
        const candidatePositions: Array<'before' | 'after'> = [];
        if (isCaretWordStart) {
          candidatePositions.push('before');
        }
        if (isCaretWordEnd) {
          candidatePositions.push('after');
        }

        for (const position of candidatePositions) {
          const didAddLineBreak = addCaptionWordLineBreakWithinAsset({
            itemId,
            wordIndex: currentWordIndex,
            position,
            setState,
          });

          if (didAddLineBreak) {
            event.currentTarget.blur();
            event.nativeEvent.stopImmediatePropagation?.();
            return;
          }
        }

        return;
      }

      const isBackspaceAtWordStart =
        event.key === 'Backspace' && isCaretWordStart;

      if (isBackspaceAtWordStart && currentWordIndex > 0) {
        // 개행된 단어 바로 다음에서 Backspace면 기존 단어 병합 대신 newline만 제거한다.
        const didRemoveLineBreak = removeCaptionWordLineBreakWithinAsset({
          itemId,
          wordIndex: currentWordIndex,
          setState,
        });

        if (didRemoveLineBreak) {
          event.preventDefault();
          event.currentTarget.blur();
          event.nativeEvent.stopImmediatePropagation?.();
          return;
        }
      }

      if (event.key !== 'Enter' && !isBackspaceAtWordStart) {
        return;
      }

      event.preventDefault();

      if (isBackspaceAtWordStart && currentWordIndex > 0) {
        // 단어 첫머리에서 백스페이스면 동일 캡션 내 앞 단어와 병합을 시도.
        const didMerge = mergeCaptionWordWithinAsset({
          itemId,
          wordIndex: currentWordIndex,
          setState,
        });

        if (didMerge) {
          event.currentTarget.blur();
          event.nativeEvent.stopImmediatePropagation?.();
          return;
        }
      }

      if (sanitizedBefore !== normalizedWord) {
        toast({
          type: 'error',
          description: t('Editor.toast.divide-caption'),
          toastOptions: {
            position: 'top-center',
          },
        });
        return;
      }

      if (event.key === 'Enter') {
        let splitIndex: number | null = null;

        if (isCaretWordStart && currentWordIndex > 0) {
          splitIndex = currentWordIndex;
        } else if (isCaretWordEnd && currentWordIndex < words.length - 1) {
          splitIndex = currentWordIndex + 1;
        }

        if (splitIndex == null) {
          return;
        }

        splitCaptionItem({
          itemId,
          splitIndex,
          trackIndex: captionTrackIndex,
          itemIndex: currentIndex,
          setState,
        });

        event.currentTarget.blur();
        event.nativeEvent.stopImmediatePropagation?.();
        return;
      }

      if (currentWordIndex > 0) {
        return;
      }

      const prevItemId = trackItems[currentIndex - 1];
      if (!prevItemId) {
        return;
      }

      const prevContext = captionContexts.get(prevItemId);
      if (!prevContext) {
        return;
      }

      if (prevContext.sceneId !== context.sceneId) {
        toast({
          type: 'error',
          description: t('Editor.toast.merge-scene-error'),
          toastOptions: {
            position: 'top-center',
          },
        });
        return;
      }

      const didMerge = handleMergeCaptionItems(prevItemId, itemId);
      if (!didMerge) {
        return;
      }

      event.currentTarget.blur();
      event.nativeEvent.stopImmediatePropagation?.();
    },
    [
      captionContexts,
      isEditable,
      items,
      currentStateRef,
      focusWordElement,
      handleMergeCaptionItems,
      isLinkingEnabled,
      setState,
      t,
      tracks,
    ],
  );

  /**
   * contentEditable 입력 시 불필요한 줄바꿈을 제거하고 상태를 동기화한다.
   * @param event contentEditable change 이벤트 객체
   * @param originWord 렌더 시점 단어 문자열
   * @param itemId 대상 captions item id
   * @param wordIndex 렌더 시점 단어 index
   */
  const handleWordInput = useCallback(
    (
      event: React.InputEvent<HTMLParagraphElement>,
      originWord: string,
      itemId: string,
      wordIndex: number,
    ) => {
      const { currentTarget } = event;
      const originalContent = currentTarget.textContent ?? '';
      const sanitizedContent = originalContent.replace(/\r?\n/g, ' ');

      if (sanitizedContent !== originalContent) {
        currentTarget.textContent = sanitizedContent;
        if (typeof window !== 'undefined') {
          const selection = window.getSelection();
          if (selection) {
            const focusNode = currentTarget.firstChild ?? currentTarget;
            selection.collapse(focusNode, sanitizedContent.length);
          }
        }
      }

      handleWordSubmit(itemId, wordIndex, originWord, sanitizedContent);
    },
    [handleWordSubmit],
  );

  const visibleCaptionItemIds = useMemo(() => getVisibleCaptionPanelItemIds(tracks, items), [tracks, items]);
  const playback = useMemo(() => projectCaptionPanelPlayback({
    visibleItemIds: visibleCaptionItemIds,
    captionContexts,
    currentFrame,
    fps,
    convertMsToFrame,
  }), [visibleCaptionItemIds, captionContexts, currentFrame, fps]);
  const activeWordId = playback.firstActiveWordId;

  /**
   * Link가 활성화된 상태에서 캡션 아이템을 삭제합니다.
   * 같은 시간대에 있는 연결된 클립들도 함께 삭제됩니다.
   */
  const handleDeleteCaptionItem = useCallback(
    (itemId: string) => {
      setState({
        update: (state) =>
          deleteItems(state, [itemId], {
            isLinkingEnabled,
          }),
        commitToUndoStack: true,
      });
    },
    [setState, isLinkingEnabled],
  );

  const captionScenes = useMemo(() => {
    return getCaptionPanelSceneGroups(tracks, (itemId) => {
      const context = captionContexts.get(itemId);
      return context
        ? { sceneId: context.sceneId, startMs: context.videoStart }
        : null;
    });
  }, [captionContexts, tracks]);

  /* 스크립트 패널 내에서 activeWordId가 변경되면 스크롤을 이동시키는 부분 */
  useEffect(() => {
    const sectionElement = sectionRef.current;
    if (!sectionElement) return;

    const activeWordElement = sectionElement.querySelector(
      activeWordId
        ? `[data-word-id="${activeWordId}"]`
        : '[data-active-word="true"]',
    ) as HTMLElement | null;

    if (!activeWordElement) {
      lastScrolledWordIdRef.current = null;
      return;
    }

    const elementWordId = activeWordElement.getAttribute('data-word-id');
    if (!elementWordId) return;
    if (lastScrolledWordIdRef.current === elementWordId) return;

    lastScrolledWordIdRef.current = elementWordId;
    activeWordElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeWordId]);

  useEffect(() => {
    setIsShowTranscriptOnly(isTranscriptOnlyProps);
  }, [isTranscriptOnlyProps]);

  const setCaptionLinkMode = useCallback((linked:boolean) => {
    setState({update:state=>{
      if(state.isLinkingEnabled===linked)return state;
      const next=toggleLinking(state);
      saveLinkingEnabled(next.isLinkingEnabled,projectType);
      return next;
    },commitToUndoStack:false});
  },[setState,projectType]);

  const deleteCaptionLabel = t('Editor.action.delete-linked-clips');
  const mergeLabel = t('Editor.caption-panel.merge');
  const unmergeLabel = t('Editor.caption-panel.unmerge');

  return (
    <div className="absolute top-0 left-0 flex h-full w-full flex-col gap-xs p-md">
      {isShowTranscriptCheckbox && (
        <header className="flex flex-wrap items-center gap-sm">
          <div className="min-w-fit shrink-0" onClickCapture={event=>{
            // Tooltip owns the focusable button; keyboard clicks do not descend into Chip's div.
            if(event.target instanceof HTMLButtonElement){event.preventDefault();event.stopPropagation();setCaptionLinkMode(true);}
          }}>
            <Tooltip
              textNode={t('Editor.script-wrapper.video-edit-mode-tooltip')}
            >
              <Chip
                labelNode={t('Editor.script-wrapper.video-edit-mode')}
                selected={isLinkingEnabled}
                onClick={() => setCaptionLinkMode(true)}
                isShowTrailingIcon={true}
                trailingIconNode={
                  <HelpFill className="size-4 [&_path]:fill-current" />
                }
              />
            </Tooltip>
          </div>
          <div className="min-w-fit shrink-0" onClickCapture={event=>{
            if(event.target instanceof HTMLButtonElement){event.preventDefault();event.stopPropagation();setCaptionLinkMode(false);}
          }}>
            <Tooltip
              textNode={t('Editor.script-wrapper.caption-edit-mode-tooltip')}
            >
              <Chip
                labelNode={t('Editor.script-wrapper.caption-edit-mode')}
                selected={!isLinkingEnabled}
                onClick={() => setCaptionLinkMode(false)}
                isShowTrailingIcon={true}
                trailingIconNode={
                  <HelpFill className="size-4 [&_path]:fill-current" />
                }
              />
            </Tooltip>
          </div>
          <div className="min-w-fit shrink-0">
            <Checkbox
              id="transcript-only"
              label={t('Editor.script-wrapper.transcript-only')}
              status="default"
              size="medium"
              checked={isShowTranscriptOnly}
              onCheckedChange={(checked: boolean) =>
                setIsShowTranscriptOnly(Boolean(checked))
              }
            />
          </div>
        </header>
      )}
      <section
        ref={sectionRef}
        className="no-scrollbar overflow-y-auto layout-padding-bottom"
      >
        {captionScenes.map(({
          sceneId,
          itemIds,
          trackLocked,
          trackHidden,
        }, groupIndex) => {
          const sceneLabel = sceneId != null ? `Scene${sceneId}` : 'Scene-';

          return (
            <div key={`${sceneLabel}-${groupIndex}`}>
              <Label
                color={'neutral'}
                size={'medium'}
                className={'mb-2xs'}
              >
                {sceneLabel}
              </Label>
              {itemIds.map((id) => {
                const context = captionContexts.get(id);
                if (!context) return null;
                return (
                  <CaptionItemRow
                    key={id}
                    id={id}
                    context={context}
                    nextItemId={captionNextItemIds.get(id)}
                    activeWordIndexes={playback.activeWordIndexesByItem.get(id) ?? ''}
                    trackLocked={trackLocked}
                    trackHidden={trackHidden}
                    isShowTranscriptOnly={isShowTranscriptOnly}
                    isEditable={isEditable}
                    isLinkingEnabled={isLinkingEnabled}
                    fullState={fullState}
                    projectType={projectType}
                    deleteLabel={deleteCaptionLabel}
                    mergeLabel={mergeLabel}
                    unmergeLabel={unmergeLabel}
                    onDeleteCaptionItem={handleDeleteCaptionItem}
                    onMergeCaptionItems={handleMergeCaptionItems}
                    onUnmergeCaptionItems={handleUnmergeCaptionItems}
                    onWordClick={handleWordClick}
                    onWordKeyDown={handleWordKeyDown}
                    onWordInput={handleWordInput}
                  />
                );
              })}
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default CaptionsContent;
